import { Injectable, inject } from '@angular/core';
import { TenantDto } from '@nusign/picflow-backend-types';
import { Observable, combineLatest, map, switchMap, take, tap } from 'rxjs';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { unique } from '../lib/unique';
import { DataLoaderService } from './data-loader.service';
import { FoldersCommonService } from './folders-common.service';
import { GalleriesCommonService } from './galleries-common.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';
import { SharedWithMeFolderService } from './shared-with-me-folder.service';
import { SharedWithMeGalleryService } from './shared-with-me-gallery.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { UpdateService } from './update.service';

@Injectable({
  providedIn: 'root',
})
export class SharedWithMeTenantService {
  private sharedWithMeGalleryService = inject(SharedWithMeGalleryService);
  private sharedWithMeFolderService = inject(SharedWithMeFolderService);
  private galleriesCommonService = inject(GalleriesCommonService);
  private permissionService = inject(PermissionService);
  private foldersCommonService = inject(FoldersCommonService);
  private state = inject(StateService);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private updateService = inject(UpdateService);
  private storage = inject(StorageService);

  public loadSharedWithMeTenants(): Observable<TenantDto[]> {
    return combineLatest([
      this.sharedWithMeGalleryService.getSharedWithMeGalleriesRaw(),
      this.sharedWithMeFolderService.getSharedWithMeFoldersRaw(),
    ]).pipe(
      map(([galleries, folders]) => {
        const tenantIds = unique([
          ...galleries.map(gallery => gallery.tenant as string),
          ...folders.map(folder => folder.tenant as string),
        ]);

        return tenantIds.map(id => {
          return {
            tenantId: id,
            galleryId: galleries.find(gallery => gallery.tenant === id)?.id,
            folderId: folders.find(folder => folder.tenant === id)?.id,
          };
        });
      }),
      switchMap(resp => {
        return forkJoinSafe(
          resp.map(data => {
            if (!data.galleryId) {
              return this.foldersCommonService.getTenantInfoFromFolder(data.folderId, data.tenantId).pipe(
                take(1),
                map(info => info.tenant)
              );
            }
            return this.galleriesCommonService.getTenantInfoFromGallery(data.galleryId, data.tenantId).pipe(
              take(1),
              map(info => info.tenant)
            );
          })
        );
      }),
      tap(tenants => {
        this.state.sharedWithMeTenants = tenants.filter(tenant => !this.state.tenants?.some(t => t.id === tenant.id));
        const defaultTenant = this.getDefaultTenant(true);
        if (defaultTenant) this.setActiveSharedWithMeTenant(defaultTenant);
      })
    );
  }

  public setActiveSharedWithMeTenant(tenant: TenantDto) {
    this.storage.remove('activeSharedWithMeTenant');
    this.storage.remove('backupSharedWithMeTenantId');
    this.storage.remove('activeTenantId');
    this.storage.remove('backupTenantId');

    if (tenant) {
      this.storage.set('activeSharedWithMeTenant', tenant.id, true);
      this.storage.set('backupSharedWithMeTenantId', tenant.id);
    }

    if (
      this.state.activeTenant ||
      (this.state.sharedWithMeActiveTenant && this.state.sharedWithMeActiveTenant.id !== tenant.id)
    ) {
      this.loader.clear();
      this.observableLoader.clear();
      this.updateService.clear();
    }
    this.state.sharedWithMeActiveTenant = tenant;
    this.state.sharedWithMeActiveTenant$.next(tenant);
    this.state.activeTenant = undefined;
    this.state.activeTenantRole = undefined;
    this.permissionService.setRolePermissions(undefined);
    this.state.plan = undefined;
    this.state.plan$.next(undefined);
  }

  public getDefaultTenant(forAutoSelect: boolean): TenantDto {
    if (forAutoSelect) {
      if (this.state.activeTenant) return;
      if (this.storage.get('activeTenantId') || this.storage.get('backupTenantId')) return;
    }
    const storageTenantId =
      this.storage.get('activeSharedWithMeTenant') || this.storage.get('backupSharedWithMeTenantId');
    if (storageTenantId) {
      const tenant = this.state.sharedWithMeTenants.find(t => t.id === storageTenantId);
      if (tenant) return tenant;
    }
    if (forAutoSelect) return;
    if (this.state.sharedWithMeTenants.length === 1) return this.state.sharedWithMeTenants[0];
  }
}
